
import { Vector2D, Ball, SimulationConfig, ShapeType } from './types';
import { ShapeUtils } from './ShapeUtils';

export const getDistance = (v1: Vector2D, v2: Vector2D): number => {
  return Math.sqrt((v1.x - v2.x) ** 2 + (v1.y - v2.y) ** 2);
};

export const getRandomColor = (): string => {
  const colors = [
    '#ef4444', '#f97316', '#f59e0b', '#10b981', 
    '#3b82f6', '#6366f1', '#8b5cf6', '#d946ef', 
    '#ec4899', '#f43f5e'
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

export const resolveBallCollision = (b1: Ball, b2: Ball, restitution: number): number => {
  const dx = b2.pos.x - b1.pos.x;
  const dy = b2.pos.y - b1.pos.y;
  const distanceSq = dx * dx + dy * dy;
  const minDistance = b1.radius + b2.radius;

  if (distanceSq < minDistance * minDistance) {
    const distance = Math.sqrt(distanceSq);
    
    let nx = 1;
    let ny = 0;
    
    if (distance > 0) {
      nx = dx / distance;
      ny = dy / distance;
    } else {
      const angle = Math.random() * Math.PI * 2;
      nx = Math.cos(angle);
      ny = Math.sin(angle);
    }

    const overlap = minDistance - distance;
    const moveX = nx * overlap / 2;
    const moveY = ny * overlap / 2;
    
    b1.pos.x -= moveX;
    b1.pos.y -= moveY;
    b2.pos.x += moveX;
    b2.pos.y += moveY;

    const v1n = b1.vel.x * nx + b1.vel.y * ny;
    const v2n = b2.vel.x * nx + b2.vel.y * ny;

    const relVel = v1n - v2n;

    if (relVel > 0) {
      const impulse = (1 + restitution) * relVel / (1 / b1.mass + 1 / b2.mass);
      
      b1.vel.x -= (impulse / b1.mass) * nx;
      b1.vel.y -= (impulse / b1.mass) * ny;
      b2.vel.x += (impulse / b2.mass) * nx;
      b2.vel.y += (impulse / b2.mass) * ny;
      
      const kick = 0.05;
      b1.vel.x += (Math.random() - 0.5) * kick;
      b1.vel.y += (Math.random() - 0.5) * kick;
      b2.vel.x += (Math.random() - 0.5) * kick;
      b2.vel.y += (Math.random() - 0.5) * kick;

      return relVel; // Return impact magnitude
    }
  }
  return 0; // No collision or separating
};

export interface MorphData {
    from: ShapeType;
    to: ShapeType;
    factor: number;
    baseRadius?: number; // Enable additive offset logic
}

export const resolveWallCollision = (
  ball: Ball, 
  centerX: number, 
  centerY: number, 
  radius: number, 
  config: SimulationConfig,
  morph?: MorphData
): number => {
  // Relative position
  const relX = ball.pos.x - centerX;
  const relY = ball.pos.y - centerY;
  
  const shape = morph ? morph.from : (config.containerShape || 'CIRCLE');
  const targetShape = morph ? morph.to : shape;
  const t = morph ? morph.factor : 0;
  
  // Standard optimized circle logic ONLY if not morphing and shape is circle
  if (shape === 'CIRCLE' && targetShape === 'CIRCLE') {
      const dist = Math.sqrt(relX * relX + relY * relY);
      if (dist + ball.radius > radius) {
        const nx = relX / dist;
        const ny = relY / dist;
        const overlap = (dist + ball.radius) - radius;
        ball.pos.x -= nx * overlap;
        ball.pos.y -= ny * overlap;
        return calculateBounce(ball, nx, ny, config);
      }
  } else {
      // General Polygon / Shape Logic using SDF
      const angle = Math.atan2(relY, relX);
      const rotRad = (config.containerRotation || 0) * Math.PI / 180;
      
      // We check radius at current angle relative to shape rotation
      const boundaryRadius = ShapeUtils.getInterpolatedRadiusAtAngle(angle - rotRad, shape, targetShape, t, radius, morph?.baseRadius);
      const dist = Math.sqrt(relX*relX + relY*relY);
      
      if (dist + ball.radius > boundaryRadius) {
          // Collision detected!
          
          // To get accurate normal for rotated shape:
          // 1. Rotate relative position INTO local shape space (-rotRad)
          const localX = relX * Math.cos(-rotRad) - relY * Math.sin(-rotRad);
          const localY = relX * Math.sin(-rotRad) + relY * Math.cos(-rotRad);
          
          // 2. Get local normal (interpolated)
          const localNormal = ShapeUtils.getNormal({x: localX, y: localY}, shape, radius, targetShape, t, morph?.baseRadius);
          
          // 3. Rotate normal BACK to world space (+rotRad)
          const worldNx = localNormal.x * Math.cos(rotRad) - localNormal.y * Math.sin(rotRad);
          const worldNy = localNormal.x * Math.sin(rotRad) + localNormal.y * Math.cos(rotRad);
          
          // Push back along normal
          const overlap = (dist + ball.radius) - boundaryRadius;
          // Approximate push back
          ball.pos.x -= worldNx * overlap;
          ball.pos.y -= worldNy * overlap;
          
          return calculateBounce(ball, worldNx, worldNy, config);
      }
  }
  
  return 0;
};

// Helper to centralize bounce physics
const calculateBounce = (ball: Ball, nx: number, ny: number, config: SimulationConfig): number => {
    const dot = ball.vel.x * nx + ball.vel.y * ny;
    if (dot > 0) {
      const MIN_BOUNCE_VELOCITY = 2.5; 
      let impulse = 2 * dot;
      if (dot < MIN_BOUNCE_VELOCITY) impulse = dot + MIN_BOUNCE_VELOCITY;

      ball.vel.x -= impulse * nx;
      ball.vel.y -= impulse * ny;

      let speed = Math.sqrt(ball.vel.x ** 2 + ball.vel.y ** 2);
      if (config.restitution > 1) speed += (config.restitution - 1) * 0.8; 
      else if (config.restitution < 1) speed *= config.restitution;

      const isAtBottom = ny > 0.8; 
      const MIN_HORIZONTAL = 2.5 * config.antiStallMultiplier;
      if (isAtBottom && Math.abs(ball.vel.x) < MIN_HORIZONTAL) {
        ball.vel.x = (ball.vel.x >= 0 ? 1 : -1) * (MIN_HORIZONTAL + Math.random() * 2);
      }

      const MAX_SPEED = config.maxVelocityLimit;
      const MIN_SPEED = config.minVelocityLimit; 
      if (speed > MAX_SPEED) speed = MAX_SPEED;
      else if (speed < MIN_SPEED) speed = MIN_SPEED;

      const currentSpeed = Math.sqrt(ball.vel.x ** 2 + ball.vel.y ** 2);
      if (currentSpeed > 0) {
        ball.vel.x = (ball.vel.x / currentSpeed) * speed;
        ball.vel.y = (ball.vel.y / currentSpeed) * speed;
      }
      return dot;
    }
    return 0;
};
